import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, categoriesAPI, situationsAPI, landingsAPI, adminCategoriesAPI, adminSectionsAPI, adminSituationsAPI, mediaAPI, adminVendorsAPI, adminNewsAPI } from '../lib/api';
import { slugifyCategoryId, slugifySectionSlug, categoryOptionsForProducts } from '../lib/categoryId';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useToast } from '../hooks/use-toast';
import { Trash2, Edit, Plus, Upload, Download, X, File, ExternalLink, Phone, MessageSquare, Bot, BarChart3, BookOpen, Link as LinkIcon, Package, Box, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import MediaManager from '../components/MediaManager';
import RichTextEditor from '../components/RichTextEditor';
import { blogList } from '../data/blogPosts';
import AdminNewsTab from '../components/admin/AdminNewsTab';
import AdminSubscriptionsTab from '../components/admin/subscriptions/AdminSubscriptionsTab';
import AdminSeoFields from '../components/admin/AdminSeoFields';
import { buildVendorSeo } from '../lib/catalogSeo';
import { canAccessAdminSection, getDefaultAdminTab } from '../lib/adminPermissions';

const LUCIDE_ICONS = [
  { value: 'Phone', label: 'Phone', Icon: Phone },
  { value: 'MessageSquare', label: 'MessageSquare', Icon: MessageSquare },
  { value: 'Bot', label: 'Bot', Icon: Bot },
  { value: 'BarChart3', label: 'BarChart3', Icon: BarChart3 },
  { value: 'BookOpen', label: 'BookOpen', Icon: BookOpen },
  { value: 'Link', label: 'Link', Icon: LinkIcon },
  { value: 'Package', label: 'Package', Icon: Package },
  { value: 'Box', label: 'Box', Icon: Box },
];

const isLucideIcon = (value) => {
  return LUCIDE_ICONS.some(icon => icon.value === value);
};

const ADMIN_TAB_PERMISSIONS = {
  products: 'products',
  vendors: 'vendors',
  news: 'news',
  landings: 'landings',
  homeSettings: 'settings',
  orders: 'orders',
  cart: 'cart',
  purchases: 'purchases',
  subscriptions: 'subscriptions',
};

/** Полное начальное состояние формы продукта (все поля строк/массивов заданы — без undefined в controlled inputs). */
function emptyProductForm() {
  return {
    name: '',
    short_description: '',
    full_description: '',
    category: '',
    section_id: '',
    section_ids: [],
    vendor: '',
    sku: '',
    icon: '',
    price: '',
    landing_url: '',
    tags: [],
    features: [],
    situations: [],
    template_type: 'legacy',
    hero_content: {
      badge_text: '',
      title: '',
      subtitle: '',
      summary: '',
      media_type: 'image',
      media_url: '',
      media_caption: '',
      media_secondary_text: '',
    },
    description_blocks: [],
    benefits: [],
    features_v2: [],
    how_it_works_steps: [],
    integrations: [],
    faq_items: [],
    final_cta: {
      badge_text: '',
      title: '',
      text: '',
      button_label: '',
      secondary_button_label: '',
      price_text: '',
    },
    documents: [],
    case_studies: [],
    hero_title: '',
    hero_description: '',
    meta_title: '',
    meta_description: '',
    ai_summary: '',
    ai_use_cases: [],
    ai_features: [],
    ai_pricing_hint: '',
    ai_faq: [],
    ai_keywords: '',
    ai_benefits_text: '',
    import_substitution: [],
    calculator_config: {
      basePricePerUser: 0,
      additionalServices: [],
      licenses: [],
      showUserCount: true,
      featuredNewsSlug: '',
      vendor_info: {
        materials: [],
        deliveryTerms: [],
      },
    },
  };
}

const AdminPanelPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [adminPermissions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('adminPermissions') || '[]');
    } catch {
      return [];
    }
  });
  const [activeTab, setActiveTab] = useState(() => getDefaultAdminTab(adminPermissions));
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [situations, setSituations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [landings, setLandings] = useState([]);
  const [filterSituations, setFilterSituations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingLanding, setEditingLanding] = useState(null);
  const [productFiles, setProductFiles] = useState({});
  const [landingFiles, setLandingFiles] = useState({});
  const [editingSituation, setEditingSituation] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [editingVendor, setEditingVendor] = useState(null);
  
  // Форма вендора
  const [vendorForm, setVendorForm] = useState({
    name: '',
    slug: '',
    logo: '',
    certificate: '',
    description: '',
    full_description: '',
    is_active: true,
    is_top: false,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    seo_primary_query: '',
  });
  
  // Выбор продукта
  const [selectedProductIndex, setSelectedProductIndex] = useState('');
  
  // Форма продукта
  const [productForm, setProductForm] = useState(() => emptyProductForm());
  const [newSolutionFeature, setNewSolutionFeature] = useState({ title: '', description: '', group: 'core', order: 1 });
  const [newTag, setNewTag] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newSituation, setNewSituation] = useState('');
  const [newAdditionalService, setNewAdditionalService] = useState({ name: '', price: '', isMonthly: false });
  const [newLicense, setNewLicense] = useState({ name: '', description: '', delivery: '', minimumOrder: '', price: '' });
  const [newVendorDeliveryTerm, setNewVendorDeliveryTerm] = useState({ condition: '', value: '' });
  const [importFile, setImportFile] = useState(null);
  const [importingProducts, setImportingProducts] = useState(false);
  const [exportingProducts, setExportingProducts] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [authError, setAuthError] = useState('');
  
  // Форма лендинга
  const [landingForm, setLandingForm] = useState({
    slug: '',
    title: '',
    hero_title: '',
    hero_description: '',
    meta_description: '',
    is_active: true,
    services: [],
    benefits: [],
    tags: [],
    situations: [],
  });
  const [newService, setNewService] = useState({ service_id: '', name: '', description: '', icon: '', price: '', price_unit: '/мес' });
  const [newBenefit, setNewBenefit] = useState({ title: '', description: '', icon: '' });
  const [newLandingTag, setNewLandingTag] = useState('');
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [situationForm, setSituationForm] = useState({
    id: '',
    name: '',
    icon: '',
    description: '',
  });
  const [categoryForm, setCategoryForm] = useState({
    id: '',
    name: '',
    icon: '',
    description: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [filterCategories, setFilterCategories] = useState([]);
  const [sectionForm, setSectionForm] = useState({
    category_id: '',
    slug: '',
    name: '',
    icon: '',
    description: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });
  const [editingSection, setEditingSection] = useState(null);
  const [adminSectionsList, setAdminSectionsList] = useState([]);
  const [catalogSectionsForAdmin, setCatalogSectionsForAdmin] = useState([]);
  const [newsPublications, setNewsPublications] = useState([]);
  // Ситуации для лендинга
  const [landingSituations, setLandingSituations] = useState([]);

  // Функция для перевода статусов на русский
  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': 'В ожидании',
      'processing': 'В обработке',
      'completed': 'Завершен',
      'cancelled': 'Отменен'
    };
    return statusMap[status] || status;
  };

  useEffect(() => {
    // Запрет индексации
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setAuthError('');
      if (activeTab === 'products') {
        const [productsData, categoriesData, situationsData, vendorsData, sectionsAdm, newsData] = await Promise.all([
          adminAPI.getProducts(),
          categoriesAPI.getCategories(),
          situationsAPI.getSituations(),
          adminVendorsAPI.getVendors().catch(() => []),
          adminSectionsAPI.getAll().catch(() => []),
          adminNewsAPI.getAll().catch(() => []),
        ]);
        setProducts(productsData || []);
        setCategories(categoriesData || []);
        setSituations(situationsData || []);
        setVendors(vendorsData || []);
        setCatalogSectionsForAdmin(sectionsAdm || []);
        setNewsPublications(newsData || []);
      } else if (activeTab === 'vendors') {
        const vendorsData = await adminVendorsAPI.getVendors();
        setVendors(vendorsData || []);
      } else if (activeTab === 'homeSettings') {
        const [situationsData, categoriesData, sectionsData] = await Promise.all([
          adminSituationsAPI.getAll(),
          adminCategoriesAPI.getAll(),
          adminSectionsAPI.getAll(),
        ]);
        setFilterSituations(situationsData || []);
        setFilterCategories(categoriesData || []);
        setAdminSectionsList(sectionsData || []);
      } else if (activeTab === 'landings') {
        const [landingsData, situationsData] = await Promise.all([
          landingsAPI.getLandings(),
          adminSituationsAPI.getAll(),
        ]);
        setLandings(landingsData || []);
        setLandingSituations(situationsData || []);
      } else if (activeTab === 'orders') {
        const ordersData = await adminAPI.getOrders();
        setOrders(ordersData || []);
      } else if (activeTab === 'cart') {
        console.log('Loading cart orders...');
        const adminToken = localStorage.getItem('adminToken');
        if (!adminToken) {
          console.error('Admin token not found!');
          toast({
            title: "Ошибка авторизации",
            description: "Токен администратора не найден. Пожалуйста, войдите в систему.",
            variant: "destructive",
          });
          setCartOrders([]);
          return;
        }
        try {
          const cartData = await adminAPI.getCartOrders();
          console.log('Cart orders loaded:', cartData);
          console.log('Cart orders type:', typeof cartData);
          console.log('Cart orders is array:', Array.isArray(cartData));
          if (Array.isArray(cartData)) {
            console.log('Cart orders count:', cartData.length);
            setCartOrders(cartData);
          } else {
            console.error('Cart orders is not an array:', cartData);
            setCartOrders([]);
          }
        } catch (err) {
          console.error('Error loading cart orders:', err);
          console.error('Error stack:', err.stack);
          setCartOrders([]);
          throw err; // Re-throw to be caught by outer catch
        }
      } else if (activeTab === 'purchases') {
        const purchasesData = await adminAPI.getPurchases();
        setPurchases(purchasesData || []);
      }
    } catch (error) {
      console.error('Error in loadData:', error);
      const errorMessage = error.message || 'Ошибка при загрузке данных';
      console.error('Error message:', errorMessage);
      if (String(errorMessage).includes('Not authenticated')) {
        setAuthError('Требуется повторная авторизация администратора.');
      }
      toast({
        title: "Ошибка",
        description: errorMessage,
        variant: "destructive",
      });
      // Устанавливаем пустые массивы при ошибке, чтобы не показывать старые данные
      if (activeTab === 'cart') {
        setCartOrders([]);
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, toast]);

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/utech/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (activeTab) {
      loadData();
    }
  }, [activeTab, loadData]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminId');
    localStorage.removeItem('adminPermissions');
    navigate('/utech/login');
  };

  /** Иконка-URL: в БД храним путь /images/vendors/... (короткий), абсолютные URL из старых данных приводим к пути. */
  const normalizeProductIconForApi = (icon) => {
    if (icon == null || typeof icon !== 'string') return icon;
    const t = icon.trim();
    if (!t) return '';
    if (t.startsWith('/images/vendors/')) return t.slice(0, 2048);
    if (/^https?:\/\//i.test(t)) {
      try {
        const { pathname } = new URL(t);
        if (pathname.startsWith('/images/vendors/')) return pathname.slice(0, 2048);
      } catch {
        /* not a valid URL */
      }
    }
    return t.slice(0, 2048);
  };

  const handleCreateProduct = async () => {
    try {
      setLoading(true);
      const payload = {
        ...productForm,
        icon: normalizeProductIconForApi(productForm.icon),
        section_ids: productForm.section_ids || [],
        section_id: (productForm.section_ids && productForm.section_ids[0]) || null,
        import_substitution: (productForm.import_substitution || [])
          .map((item) => String(item || '').trim())
          .filter(Boolean),
        calculator_config: {
          basePricePerUser: Number(productForm.calculator_config?.basePricePerUser || 0),
          additionalServices: (productForm.calculator_config?.additionalServices || []).map((s) => ({
            ...s,
            price: Number(s.price || 0),
          })),
          licenses: (productForm.calculator_config?.licenses || []).map((license) => ({
            ...license,
            price: Number(license.price || 0),
          })),
          showUserCount: productForm.calculator_config?.showUserCount !== undefined ? productForm.calculator_config.showUserCount : true,
          featuredNewsSlug: productForm.calculator_config?.featuredNewsSlug || '',
          vendor_info: {
            materials: productForm.calculator_config?.vendor_info?.materials || [],
            deliveryTerms: productForm.calculator_config?.vendor_info?.deliveryTerms || [],
          },
        },
      };
      if ('section' in payload) delete payload.section;
      await adminAPI.createProduct(payload);
      toast({
        title: "Успех",
        description: "Продукт успешно создан",
      });
      resetProductForm();
      loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const description =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join('; ')
            : error.message || 'Ошибка при создании продукта';
      toast({
        title: "Ошибка",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    try {
      setLoading(true);
      const payload = {
        ...productForm,
        icon: normalizeProductIconForApi(productForm.icon),
        section_ids: productForm.section_ids || [],
        section_id: (productForm.section_ids && productForm.section_ids[0]) || null,
        import_substitution: (productForm.import_substitution || [])
          .map((item) => String(item || '').trim())
          .filter(Boolean),
        calculator_config: {
          basePricePerUser: Number(productForm.calculator_config?.basePricePerUser || 0),
          additionalServices: (productForm.calculator_config?.additionalServices || []).map((s) => ({
            ...s,
            price: Number(s.price || 0),
          })),
          licenses: (productForm.calculator_config?.licenses || []).map((license) => ({
            ...license,
            price: Number(license.price || 0),
          })),
          showUserCount: productForm.calculator_config?.showUserCount !== undefined ? productForm.calculator_config.showUserCount : true,
          featuredNewsSlug: productForm.calculator_config?.featuredNewsSlug || '',
          vendor_info: {
            materials: productForm.calculator_config?.vendor_info?.materials || [],
            deliveryTerms: productForm.calculator_config?.vendor_info?.deliveryTerms || [],
          },
        },
      };
      if ('section' in payload) delete payload.section;
      await adminAPI.updateProduct(editingProduct.id, payload);
      toast({
        title: "Успех",
        description: "Продукт успешно обновлен",
      });
      setEditingProduct(null);
      resetProductForm();
      loadData();
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при обновлении продукта',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот продукт?')) {
      return;
    }
    try {
      setLoading(true);
      await adminAPI.deleteProduct(productId);
      toast({
        title: "Успех",
        description: "Продукт успешно удален",
      });
      loadData();
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при удалении продукта',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportProductsCsv = async () => {
    try {
      setExportingProducts(true);
      const blob = await adminAPI.exportProductsCsv();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.href = url;
      link.download = `products-export-${date}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Выгрузка завершена',
        description: 'CSV со всеми решениями сохранён на диск.',
      });
    } catch (error) {
      toast({
        title: 'Ошибка выгрузки',
        description: error.message || 'Не удалось выгрузить решения',
        variant: 'destructive',
      });
    } finally {
      setExportingProducts(false);
    }
  };

  const handleImportProducts = async () => {
    if (!importFile) {
      toast({
        title: 'Выберите файл',
        description: 'Загрузите .csv или .xlsx файл для импорта.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setImportingProducts(true);
      const report = await adminAPI.importProducts(importFile);
      setImportReport(report);
      await loadData();
      const hasRowErrors = (report.error_count || 0) > 0;
      toast({
        title: hasRowErrors ? 'Импорт с ошибками' : 'Импорт завершен',
        description: `Создано: ${report.created_count}, обновлено: ${report.updated_count ?? 0}, вендоров: ${report.vendors_created_count ?? 0}, без изменений: ${report.skipped_existing_count}, ошибок: ${report.error_count}`,
        variant: hasRowErrors ? 'destructive' : 'default',
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const description =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join('; ')
            : error.message || 'Ошибка при импорте продуктов';
      toast({
        title: 'Ошибка импорта',
        description,
        variant: 'destructive',
      });
    } finally {
      setImportingProducts(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    const calcConfig = product.calculator_config || {};
    setProductForm({
      name: product.name,
      short_description: product.short_description || '',
      full_description: product.full_description || '',
      category: product.category || '',
      section_id: product.section_id || '',
      section_ids: Array.isArray(product.section_ids) && product.section_ids.length
        ? product.section_ids
        : (product.section_id ? [product.section_id] : []),
      vendor: product.vendor || '',
      sku: product.sku || '',
      icon: product.icon || '',
      price: product.price || '',
      landing_url: product.landing_url || '',
      tags: product.tags || [],
      features: product.features || [],
      situations: product.situations || [],
      calculator_config: {
        basePricePerUser: calcConfig.basePricePerUser || 0,
        additionalServices: (calcConfig.additionalServices || []).map((service, idx) => ({
          ...service,
          id: service.id || `service-${idx}-${Date.now()}`,
        })),
        licenses: (calcConfig.licenses || []).map((license, idx) => ({
          ...license,
          id: license.id || `license-${idx}-${Date.now()}`,
        })),
        showUserCount: calcConfig.showUserCount !== undefined ? calcConfig.showUserCount : true,
        featuredNewsSlug: calcConfig.featuredNewsSlug || '',
        vendor_info: calcConfig.vendor_info ? {
          materials: calcConfig.vendor_info.materials || [],
          deliveryTerms: calcConfig.vendor_info.deliveryTerms || [],
        } : { materials: [], deliveryTerms: [] }
      },
      hero_title: product.hero_title || '',
      hero_description: product.hero_description || '',
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || '',
      ai_summary: product.ai_summary || '',
      ai_use_cases: product.ai_use_cases || [],
      ai_features: product.ai_features || [],
      ai_pricing_hint: product.ai_pricing_hint || '',
      ai_faq: product.ai_faq || [],
      ai_keywords: product.ai_keywords || '',
      ai_benefits_text: product.ai_benefits_text || '',
      import_substitution: product.import_substitution || [],
      template_type: product.template_type || (product.hero || product.description || product.final_cta ? 'solution' : 'legacy'),
      hero_content: product.hero || {
        badge_text: product.category === 'ai' ? 'AI-решение' : 'Решение для бизнеса',
        title: product.hero_title || product.name || '',
        subtitle: product.short_description || '',
        summary: product.ai_summary || product.hero_description || '',
        media_type: 'image',
        media_url: '',
        media_caption: '',
        media_secondary_text: '',
      },
      description_blocks: (product.description && product.description.blocks) || [],
      benefits: product.benefits || [],
      features_v2: product.features_v2 || [],
      how_it_works_steps: product.how_it_works_steps || [],
      integrations: product.integrations || [],
      faq_items: product.faq_items || [],
      final_cta: product.final_cta || {
        badge_text: 'Финальный шаг',
        title: 'Готовы обсудить и быстро запустить решение?',
        text: 'Оставьте заявку, чтобы получить точный состав, коммерческие условия и следующий шаг по внедрению.',
        button_label: 'Купить / обсудить',
        secondary_button_label: 'Скачать презентацию',
        price_text: product.price || '',
      },
      documents: product.documents || [],
      case_studies: product.case_studies || []
    });
    loadProductFiles(product.id);
  };

  const loadProductFiles = async (productId) => {
    try {
      const files = await adminAPI.getProductFiles(productId);
      setProductFiles(prev => ({ ...prev, [productId]: files }));
    } catch (error) {
      console.error('Error loading product files:', error);
    }
  };

  const handleFileUpload = async (productId, file) => {
    try {
      await adminAPI.uploadProductFile(productId, file);
      toast({
        title: "Успех",
        description: "Файл успешно загружен",
      });
      loadProductFiles(productId);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при загрузке файла',
        variant: "destructive",
      });
    }
  };

  const handleDeleteFile = async (productId, fileId) => {
    try {
      await adminAPI.deleteProductFile(productId, fileId);
      toast({
        title: "Успех",
        description: "Файл успешно удален",
      });
      loadProductFiles(productId);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при удалении файла',
        variant: "destructive",
      });
    }
  };

  const resetProductForm = () => {
    setProductForm(emptyProductForm());
    setEditingProduct(null);
    setNewTag('');
    setNewFeature('');
    setNewAdditionalService({ name: '', price: '', isMonthly: false });
    setNewVendorDeliveryTerm({ condition: '', value: '' });
  };

  const addTag = () => {
    if (newTag.trim()) {
      setProductForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    setProductForm(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setProductForm(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()],
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setProductForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const addSituation = async () => {
    if (!newSituation.trim()) return;
    const idFromName = newSituation.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const newId = `sit-${Date.now()}-${idFromName}`;
    const newSituationObj = { id: newId, name: newSituation.trim(), icon: '', description: '' };
    
    try {
      await adminSituationsAPI.create(newSituationObj);
      setSituations(prev => [...prev, newSituationObj]);
      setProductForm(prev => ({
        ...prev,
        situations: [...prev.situations, newId],
      }));
      toast({ title: 'Ситуация создана', description: newSituation });
    } catch (error) {
      console.error('Error creating situation:', error);
      toast({ title: 'Ошибка', description: 'Не удалось создать ситуацию', variant: 'destructive' });
      setSituations(prev => [...prev, newSituationObj]);
      setProductForm(prev => ({
        ...prev,
        situations: [...prev.situations, newId],
      }));
    }
    setNewSituation('');
  };

  const updateHeroContent = (field, value) => {
    setProductForm(prev => ({
      ...prev,
      hero_content: {
        ...(prev.hero_content || {}),
        [field]: value,
      },
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setProductForm(prev => {
      const current = Array.isArray(prev[field]) ? [...prev[field]] : [];
      current[index] = value;
      return { ...prev, [field]: current };
    });
  };

  const addArrayItem = (field, value) => {
    setProductForm(prev => ({
      ...prev,
      [field]: [...(Array.isArray(prev[field]) ? prev[field] : []), value],
    }));
  };

  const removeArrayItem = (field, index) => {
    setProductForm(prev => ({
      ...prev,
      [field]: (Array.isArray(prev[field]) ? prev[field] : []).filter((_, i) => i !== index),
    }));
  };

  const addSolutionFeature = () => {
    if (!newSolutionFeature.title.trim()) return;
    addArrayItem('features_v2', {
      ...newSolutionFeature,
      title: newSolutionFeature.title.trim(),
      description: newSolutionFeature.description.trim(),
      group: newSolutionFeature.group || 'core',
      order: Number(newSolutionFeature.order || (productForm.features_v2 || []).length + 1),
    });
    setNewSolutionFeature({ title: '', description: '', group: 'core', order: (productForm.features_v2 || []).length + 2 });
  };

  const addAdditionalService = () => {
    if (!newAdditionalService.name.trim() || newAdditionalService.price === '') return;
    const priceNumber = parseFloat(newAdditionalService.price);
    const newId = `service-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setProductForm(prev => ({
      ...prev,
      calculator_config: {
        ...prev.calculator_config,
        basePricePerUser: prev.calculator_config?.basePricePerUser || 0,
        additionalServices: [
          ...(prev.calculator_config?.additionalServices || []),
          { 
            id: newId,
            name: newAdditionalService.name.trim(), 
            price: isNaN(priceNumber) ? 0 : priceNumber,
            isMonthly: newAdditionalService.isMonthly || false,
          },
        ],
        showUserCount: prev.calculator_config?.showUserCount !== undefined ? prev.calculator_config.showUserCount : true,
        vendor_info: prev.calculator_config?.vendor_info || {
          materials: [],
          deliveryTerms: [],
        },
      },
    }));
    setNewAdditionalService({ name: '', price: '', isMonthly: false });
  };

  const addLicense = () => {
    if (!newLicense.name.trim()) return;
    const hasPrice = newLicense.price !== '' && newLicense.price !== null && newLicense.price !== undefined;
    const priceNumber = hasPrice ? parseFloat(newLicense.price) : null;
    const newId = `license-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setProductForm(prev => ({
      ...prev,
      calculator_config: {
        ...prev.calculator_config,
        licenses: [
          ...(prev.calculator_config?.licenses || []),
          {
            id: newId,
            name: newLicense.name.trim(),
            description: newLicense.description.trim(),
            delivery: newLicense.delivery.trim(),
            minimumOrder: newLicense.minimumOrder.trim(),
            price: hasPrice && !isNaN(priceNumber) ? priceNumber : null,
          },
        ],
        basePricePerUser: prev.calculator_config?.basePricePerUser || 0,
        additionalServices: prev.calculator_config?.additionalServices || [],
        showUserCount: prev.calculator_config?.showUserCount !== undefined ? prev.calculator_config.showUserCount : true,
        vendor_info: prev.calculator_config?.vendor_info || {
          materials: [],
          deliveryTerms: [],
        },
      },
    }));
    setNewLicense({ name: '', description: '', delivery: '', minimumOrder: '', price: '' });
  };

  const removeLicense = (index) => {
    setProductForm(prev => ({
      ...prev,
      calculator_config: {
        ...prev.calculator_config,
        licenses: (prev.calculator_config?.licenses || []).filter((_, i) => i !== index),
      },
    }));
  };

  const removeAdditionalService = (index) => {
    setProductForm(prev => ({
      ...prev,
      calculator_config: {
        ...prev.calculator_config,
        basePricePerUser: prev.calculator_config?.basePricePerUser || 0,
        additionalServices: (prev.calculator_config?.additionalServices || []).filter((_, i) => i !== index),
        showUserCount: prev.calculator_config?.showUserCount !== undefined ? prev.calculator_config.showUserCount : true,
        vendor_info: prev.calculator_config?.vendor_info || {
          materials: [],
          deliveryTerms: [],
        },
      },
    }));
  };

  const addVendorDeliveryTerm = () => {
    if (!newVendorDeliveryTerm.condition.trim() || !newVendorDeliveryTerm.value.trim()) return;
    setProductForm(prev => ({
      ...prev,
      calculator_config: {
        ...prev.calculator_config,
        vendor_info: {
          ...(prev.calculator_config?.vendor_info || {
            materials: [],
            deliveryTerms: [],
          }),
          deliveryTerms: [
            ...(prev.calculator_config?.vendor_info?.deliveryTerms || []),
            {
              condition: newVendorDeliveryTerm.condition.trim(),
              value: newVendorDeliveryTerm.value.trim(),
            },
          ],
        },
      },
    }));
    setNewVendorDeliveryTerm({ condition: '', value: '' });
  };

  const removeVendorDeliveryTerm = (index) => {
    setProductForm(prev => ({
      ...prev,
      calculator_config: {
        ...prev.calculator_config,
        vendor_info: {
          ...(prev.calculator_config?.vendor_info || {
            materials: [],
            deliveryTerms: [],
          }),
          deliveryTerms: (prev.calculator_config?.vendor_info?.deliveryTerms || []).filter((_, i) => i !== index),
        },
      },
    }));
  };

  // Landing functions
  const handleCreateLanding = async () => {
    try {
      setLoading(true);
      await landingsAPI.createLanding(landingForm);
      toast({
        title: "Успех",
        description: "Лендинг успешно создан",
      });
      resetLandingForm();
      loadData();
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при создании лендинга',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLanding = async () => {
    try {
      setLoading(true);
      await landingsAPI.updateLanding(editingLanding.id, landingForm);
      toast({
        title: "Успех",
        description: "Лендинг успешно обновлен",
      });
      resetLandingForm();
      loadData();
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при обновлении лендинга',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLanding = async (landingId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот лендинг?')) {
      return;
    }
    try {
      setLoading(true);
      await landingsAPI.deleteLanding(landingId);
      toast({
        title: "Успех",
        description: "Лендинг успешно удален",
      });
      loadData();
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при удалении лендинга',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditLanding = (landing) => {
    setEditingLanding(landing);
    setLandingForm({
      slug: landing.slug,
      title: landing.title,
      hero_title: landing.hero_title || '',
      hero_description: landing.hero_description || '',
      meta_description: landing.meta_description || '',
      is_active: landing.is_active,
      services: landing.services || [],
      benefits: landing.benefits || [],
      tags: landing.tags || [],
      situations: landing.situations || [],
    });
    loadLandingFiles(landing.id);
  };

  const loadLandingFiles = async (landingId) => {
    try {
      const files = await landingsAPI.getLandingFiles(landingId);
      setLandingFiles(prev => ({ ...prev, [landingId]: files }));
    } catch (error) {
      console.error('Error loading landing files:', error);
    }
  };

  const handleLandingFileUpload = async (landingId, file) => {
    try {
      await landingsAPI.uploadLandingFile(landingId, file);
      toast({
        title: "Успех",
        description: "Файл успешно загружен",
      });
      loadLandingFiles(landingId);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при загрузке файла',
        variant: "destructive",
      });
    }
  };

  const handleDeleteLandingFile = async (landingId, fileId) => {
    try {
      await landingsAPI.deleteLandingFile(landingId, fileId);
      toast({
        title: "Успех",
        description: "Файл успешно удален",
      });
      loadLandingFiles(landingId);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при удалении файла',
        variant: "destructive",
      });
    }
  };

  const resetLandingForm = () => {
    setLandingForm({
      slug: '',
      title: '',
      hero_title: '',
      hero_description: '',
      meta_description: '',
      is_active: true,
      services: [],
      benefits: [],
      tags: [],
      situations: [],
    });
    setEditingLanding(null);
    setNewService({ service_id: '', name: '', description: '', icon: '', price: '', price_unit: '/мес' });
    setNewBenefit({ title: '', description: '', icon: '' });
    setNewLandingTag('');
  };

  const addService = () => {
    if (newService.name.trim() && newService.service_id.trim()) {
      setLandingForm(prev => ({
        ...prev,
        services: [...prev.services, { ...newService, price: newService.price ? parseFloat(newService.price) : null }],
      }));
      setNewService({ service_id: '', name: '', description: '', icon: '', price: '', price_unit: '/мес' });
    }
  };

  const removeService = (index) => {
    setLandingForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const addBenefit = () => {
    if (newBenefit.title.trim()) {
      setLandingForm(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit],
      }));
      setNewBenefit({ title: '', description: '', icon: '' });
    }
  };

  const removeBenefit = (index) => {
    setLandingForm(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const addLandingTag = () => {
    if (newLandingTag.trim()) {
      setLandingForm(prev => ({
        ...prev,
        tags: [...prev.tags, newLandingTag.trim()],
      }));
      setNewLandingTag('');
    }
  };

  const removeLandingTag = (index) => {
    setLandingForm(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const productCategoryOptions = categoryOptionsForProducts(categories);

  // Categories functions
  const resetCategoryForm = () => {
    setCategoryForm({
      id: '',
      name: '',
      icon: '',
      description: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
    });
    setEditingCategory(null);
  };

  const handleCreateCategory = async () => {
    const id = (categoryForm.id || slugifyCategoryId(categoryForm.name)).trim();
    if (!id || !categoryForm.name?.trim()) {
      toast({ title: 'Ошибка', description: 'Укажите id и название категории', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      await adminCategoriesAPI.create({
        id,
        name: categoryForm.name.trim(),
        icon: categoryForm.icon || null,
        description: categoryForm.description?.trim() || null,
        meta_title: categoryForm.meta_title?.trim() || null,
        meta_description: categoryForm.meta_description?.trim() || null,
        meta_keywords: categoryForm.meta_keywords?.trim() || null,
      });
      toast({ title: 'Успех', description: 'Категория добавлена' });
      resetCategoryForm();
      loadData();
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message || 'Не удалось создать категорию', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    if (!categoryForm.name?.trim()) {
      toast({ title: 'Ошибка', description: 'Укажите название категории', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      await adminCategoriesAPI.update(editingCategory.id, {
        name: categoryForm.name.trim(),
        icon: categoryForm.icon || null,
        description: categoryForm.description?.trim() || null,
        meta_title: categoryForm.meta_title?.trim() || null,
        meta_description: categoryForm.meta_description?.trim() || null,
        meta_keywords: categoryForm.meta_keywords?.trim() || null,
      });
      toast({ title: 'Успех', description: 'Категория обновлена' });
      resetCategoryForm();
      loadData();
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message || 'Не удалось обновить категорию', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (id === 'all') {
      toast({ title: 'Ошибка', description: 'Служебную категорию «all» удалить нельзя', variant: 'destructive' });
      return;
    }
    if (!window.confirm('Удалить категорию?')) return;
    try {
      setLoading(true);
      await adminCategoriesAPI.remove(id);
      toast({ title: 'Успех', description: 'Категория удалена' });
      loadData();
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message || 'Не удалось удалить категорию', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (item) => {
    setEditingCategory(item);
    setCategoryForm({
      id: item.id,
      name: item.name || '',
      icon: item.icon || '',
      description: item.description || '',
      meta_title: item.meta_title || '',
      meta_description: item.meta_description || '',
      meta_keywords: item.meta_keywords || '',
    });
  };

  const resetSectionForm = () => {
    setSectionForm({
      category_id: '',
      slug: '',
      name: '',
      icon: '',
      description: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
    });
    setEditingSection(null);
  };

  const handleCreateSection = async () => {
    const slug = (sectionForm.slug || slugifySectionSlug(sectionForm.name)).trim();
    if (!sectionForm.category_id || !slug || !sectionForm.name?.trim()) {
      toast({ title: 'Ошибка', description: 'Укажите категорию, slug и название раздела', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      await adminSectionsAPI.create({
        category_id: sectionForm.category_id,
        slug,
        name: sectionForm.name.trim(),
        icon: sectionForm.icon || null,
        description: sectionForm.description?.trim() || null,
        meta_title: sectionForm.meta_title?.trim() || null,
        meta_description: sectionForm.meta_description?.trim() || null,
        meta_keywords: sectionForm.meta_keywords?.trim() || null,
      });
      toast({ title: 'Успех', description: 'Раздел добавлен' });
      resetSectionForm();
      loadData();
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message || 'Не удалось создать раздел', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSection = async () => {
    if (!editingSection) return;
    if (!sectionForm.name?.trim() || !sectionForm.slug?.trim()) {
      toast({ title: 'Ошибка', description: 'Заполните slug и название', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      await adminSectionsAPI.update(editingSection.id, {
        name: sectionForm.name.trim(),
        slug: sectionForm.slug.trim(),
        icon: sectionForm.icon || null,
        description: sectionForm.description?.trim() || null,
        meta_title: sectionForm.meta_title?.trim() || null,
        meta_description: sectionForm.meta_description?.trim() || null,
        meta_keywords: sectionForm.meta_keywords?.trim() || null,
      });
      toast({ title: 'Успех', description: 'Раздел обновлён' });
      resetSectionForm();
      loadData();
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message || 'Не удалось обновить раздел', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('Удалить раздел?')) return;
    try {
      setLoading(true);
      await adminSectionsAPI.remove(id);
      toast({ title: 'Успех', description: 'Раздел удалён' });
      loadData();
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message || 'Не удалось удалить раздел', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSection = (item) => {
    setEditingSection(item);
    setSectionForm({
      category_id: item.category_id || '',
      slug: item.slug || '',
      name: item.name || '',
      icon: item.icon || '',
      description: item.description || '',
      meta_title: item.meta_title || '',
      meta_description: item.meta_description || '',
      meta_keywords: item.meta_keywords || '',
    });
  };

  // Situations (filters) functions
  const resetSituationForm = () => {
    setSituationForm({
      id: '',
      name: '',
      icon: '',
      description: '',
    });
    setEditingSituation(null);
  };

  const handleCreateSituation = async () => {
    if (!situationForm.id || !situationForm.name) {
      toast({ title: "Ошибка", description: "Укажите id и название", variant: "destructive" });
      return;
    }
    try {
      setLoading(true);
      await adminSituationsAPI.create(situationForm);
      toast({ title: "Успех", description: "Ситуация добавлена" });
      resetSituationForm();
      loadData();
    } catch (error) {
      toast({ title: "Ошибка", description: error.message || 'Не удалось создать ситуацию', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSituation = async () => {
    if (!editingSituation) return;
    try {
      setLoading(true);
      const { id, ...payload } = situationForm;
      await adminSituationsAPI.update(editingSituation.id, payload);
      toast({ title: "Успех", description: "Ситуация обновлена" });
      resetSituationForm();
      loadData();
    } catch (error) {
      toast({ title: "Ошибка", description: error.message || 'Не удалось обновить ситуацию', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSituation = async (id) => {
    if (!window.confirm('Удалить ситуацию?')) return;
    try {
      setLoading(true);
      await adminSituationsAPI.remove(id);
      toast({ title: "Успех", description: "Ситуация удалена" });
      loadData();
    } catch (error) {
      toast({ title: "Ошибка", description: error.message || 'Не удалось удалить ситуацию', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSituation = (item) => {
    setEditingSituation(item);
    setSituationForm({
      id: item.id,
      name: item.name || '',
      icon: item.icon || '',
      description: item.description || '',
    });
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  /** Публичный URL файла из product_files: новые — /images/materials/..., старые — /uploads/... */
  const productFilePublicUrl = (filePath) => {
    if (!filePath) return '';
    const p = String(filePath);
    if (p.startsWith('materials/')) {
      return `/images/materials/${p.slice('materials/'.length)}`;
    }
    return `${API_BASE_URL}/uploads/${p}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Панель администратора</h1>
          <Button variant="outline" onClick={handleLogout}>
            Выйти
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {authError && (
          <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
            {authError}
          </div>
        )}
        <Tabs
          value={activeTab}
          onValueChange={(tab) => {
            if (canAccessAdminSection(adminPermissions, ADMIN_TAB_PERMISSIONS[tab])) {
              setActiveTab(tab);
            }
          }}
        >
          <TabsList className="flex h-auto w-full flex-wrap">
            {canAccessAdminSection(adminPermissions, 'products') && (
              <TabsTrigger value="products">Продукты</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'vendors') && (
              <TabsTrigger value="vendors">Вендоры</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'news') && (
              <TabsTrigger value="news">Новости</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'landings') && (
              <TabsTrigger value="landings">Комплексные решения</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'settings') && (
              <TabsTrigger value="homeSettings">Настройки сайта</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'orders') && (
              <TabsTrigger value="orders">Заказы</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'cart') && (
              <TabsTrigger value="cart">Корзина</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'purchases') && (
              <TabsTrigger value="purchases">Покупки</TabsTrigger>
            )}
            {canAccessAdminSection(adminPermissions, 'subscriptions') && (
              <TabsTrigger value="subscriptions">Подписки</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="products" className="space-y-4">
            {/* Product Selector */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle>Выбор решения</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      setSelectedProductIndex('');
                      resetProductForm();
                    }}>Создать новое</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedProductIndex}
                  onValueChange={(value) => {
                    if (!value) {
                      setSelectedProductIndex('');
                      resetProductForm();
                      return;
                    }
                    setSelectedProductIndex(value);
                    const product = products.find(p => p.id === value);
                    if (product) handleEditProduct(product);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Выберите решение для редактирования или создайте новое" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__new__">+ Создать новое решение</SelectItem>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} — {product.price || 'по запросу'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="space-y-2">
                <CardTitle>Импорт решений</CardTitle>
                <CardDescription>
                  Импорт и выгрузка CSV по одному шаблону (name, category, price, licenses, delivery_terms, import_substitution и др., без icon).
                  Пустая ячейка — поле в БД не меняется. Лицензии в колонке licenses: несколько через |,
                  поля лицензии через :: — название::описание::доставка::период::цена (цена пустая = «по запросу»).
                  Условия поставки (delivery_terms): строки через |, поля через :: — условие::значение
                  (например Период действия::по запросу|Срок поставки::3-21 рабочих дня).
                  Импортозамещение: названия программ через | (например Salesforce|HubSpot).
                  Разделы (section / раздел / разделы): несколько через | (например Безопасность конечных точек|Импортозамещение).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <Input
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={(e) => {
                      setImportFile(e.target.files?.[0] || null);
                      setImportReport(null);
                    }}
                  />
                  <Button
                    onClick={handleImportProducts}
                    disabled={importingProducts || !importFile}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {importingProducts ? 'Импорт...' : 'Импортировать'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExportProductsCsv}
                    disabled={exportingProducts}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exportingProducts ? 'Выгрузка...' : 'Выгрузить CSV'}
                  </Button>
                  <a
                    href="/templates/product-import-template.csv"
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm underline"
                  >
                    Скачать шаблон CSV
                  </a>
                </div>

                {importReport && (
                  <Alert>
                    <AlertDescription className="space-y-2">
                      <div>
                        Всего строк: {importReport.total_rows}, создано: {importReport.created_count}, обновлено: {importReport.updated_count ?? 0}, вендоров: {importReport.vendors_created_count ?? 0}, без изменений: {importReport.skipped_existing_count}, ошибок: {importReport.error_count}
                      </div>
                      {(importReport.mapped_columns || []).length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Колонки в файле: {(importReport.mapped_columns || []).join(', ')}
                        </div>
                      )}
                      {(importReport.updated || []).length > 0 && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          {(importReport.updated || []).slice(0, 15).map((item, idx) => (
                            <div key={`upd-${item.id}-${idx}`}>
                              Обновлён: {item.name} (id={item.id})
                              {item.fields_changed?.length ? ` — ${item.fields_changed.join(', ')}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                      {(importReport.warnings || []).length > 0 && (
                        <div className="text-xs text-amber-600 dark:text-amber-400 space-y-1">
                          {(importReport.warnings || []).slice(0, 10).map((item, idx) => (
                            <div key={`warn-${item.row}-${idx}`}>
                              Строка {item.row} ({item.name}): {item.message}
                            </div>
                          ))}
                        </div>
                      )}
                      {(importReport.errors || []).length > 0 && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          {(importReport.errors || []).slice(0, 10).map((item, idx) => (
                            <div key={`${item.row}-${idx}`}>
                              Строка {item.row}: {item.reason}
                            </div>
                          ))}
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{editingProduct ? 'Редактировать продукт' : 'Создать продукт'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={productForm.name || ''}
                      onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Категория</Label>
                    <Select
                      value={productForm.category || ''}
                      onValueChange={(value) => setProductForm((prev) => ({
                        ...prev,
                        category: value,
                        section_id: '',
                        section_ids: [],
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите категорию" />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategoryOptions.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Разделы</Label>
                    <Select
                      onValueChange={(value) => {
                        if (!value || value === '__none__') return;
                        setProductForm((prev) => {
                          const current = prev.section_ids || [];
                          if (current.includes(value)) return prev;
                          const next = [...current, value];
                          return {
                            ...prev,
                            section_ids: next,
                            section_id: next[0] || '',
                          };
                        });
                      }}
                      disabled={!productForm.category}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={productForm.category ? 'Добавить раздел...' : 'Сначала выберите категорию'} />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogSectionsForAdmin
                          .filter((s) => s.category_id === productForm.category)
                          .filter((s) => !(productForm.section_ids || []).includes(s.id))
                          .map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name} ({s.slug})
                            </SelectItem>
                          ))}
                        {catalogSectionsForAdmin
                          .filter((s) => s.category_id === productForm.category)
                          .filter((s) => !(productForm.section_ids || []).includes(s.id))
                          .length === 0 && (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            {productForm.category ? 'Все разделы добавлены' : 'Нет разделов'}
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    {(productForm.section_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(productForm.section_ids || []).map((sectionId) => {
                          const section = catalogSectionsForAdmin.find((s) => s.id === sectionId);
                          return (
                            <Badge key={sectionId} variant="secondary" className="flex items-center gap-1">
                              {section?.name || sectionId}
                              <button
                                type="button"
                                className="ml-1 hover:text-destructive"
                                onClick={() =>
                                  setProductForm((prev) => {
                                    const next = (prev.section_ids || []).filter((id) => id !== sectionId);
                                    return {
                                      ...prev,
                                      section_ids: next,
                                      section_id: next[0] || '',
                                    };
                                  })
                                }
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Вендор</Label>
                    <Select
                      value={productForm.vendor || ''}
                      onValueChange={(value) => setProductForm(prev => ({ ...prev, vendor: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите вендора" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.name}>
                            {vendor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Артикул (SKU)</Label>
                    <Input
                      value={productForm.sku || ''}
                      onChange={(e) => setProductForm(prev => ({ ...prev, sku: e.target.value }))}
                      placeholder="Артикул решения"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Краткое описание</Label>
                  <Textarea
                    value={productForm.short_description || ''}
                    onChange={(e) => setProductForm(prev => ({ ...prev, short_description: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Полное описание</Label>
                  <RichTextEditor
                    value={productForm.full_description || ''}
                    onChange={(value) => setProductForm(prev => ({ ...prev, full_description: value }))}
                    placeholder="Введите подробное описание решения..."
                    onUploadImage={async (file) => {
                      try {
                        const result = await mediaAPI.upload(file);
                        return result;
                      } catch (error) {
                        console.error('Upload error:', error);
                        throw error;
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Новость на странице решения</Label>
                  <Select
                    value={productForm.calculator_config?.featuredNewsSlug || '__none__'}
                    onValueChange={(value) =>
                      setProductForm((prev) => ({
                        ...prev,
                        calculator_config: {
                          ...(prev.calculator_config || {}),
                          featuredNewsSlug: value === '__none__' ? '' : value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите новость для блока справа" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Не показывать новость</SelectItem>
                      {newsPublications.map((item) => (
                        <SelectItem key={`news-${item.slug}`} value={item.slug}>
                          {item.title}
                        </SelectItem>
                      ))}
                      {blogList.map((post) => (
                        <SelectItem key={`blog-${post.slug}`} value={post.slug}>
                          {post.title} (блог)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* AI SEO Section */}
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                  <Label className="text-base font-semibold">AI SEO</Label>

                  <div className="space-y-2">
                    <Label>Шаблон страницы решения</Label>
                    <Select
                      value={productForm.template_type || 'legacy'}
                      onValueChange={(value) => setProductForm(prev => ({ ...prev, template_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите шаблон" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="legacy">Старая версия</SelectItem>
                        <SelectItem value="solution">Новая версия</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {productForm.template_type === 'solution' && (
                    <div className="space-y-6 p-4 rounded-lg border border-border/50 bg-background/60">
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Hero content</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Badge text</Label>
                            <Input value={productForm.hero_content?.badge_text || ''} onChange={(e) => updateHeroContent('badge_text', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Title</Label>
                            <Input value={productForm.hero_content?.title || ''} onChange={(e) => updateHeroContent('title', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Subtitle</Label>
                            <Input value={productForm.hero_content?.subtitle || ''} onChange={(e) => updateHeroContent('subtitle', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Summary</Label>
                            <Input value={productForm.hero_content?.summary || ''} onChange={(e) => updateHeroContent('summary', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Media type</Label>
                            <Select value={productForm.hero_content?.media_type || 'image'} onValueChange={(value) => updateHeroContent('media_type', value)}>
                              <SelectTrigger><SelectValue placeholder="Тип media" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="image">Image</SelectItem>
                                <SelectItem value="video">Video</SelectItem>
                                <SelectItem value="case">Case</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Media URL</Label>
                            <Input value={productForm.hero_content?.media_url || ''} onChange={(e) => updateHeroContent('media_url', e.target.value)} />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label>Media caption</Label>
                            <Input value={productForm.hero_content?.media_caption || ''} onChange={(e) => updateHeroContent('media_caption', e.target.value)} />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label>Media secondary text</Label>
                            <Textarea value={productForm.hero_content?.media_secondary_text || ''} onChange={(e) => updateHeroContent('media_secondary_text', e.target.value)} rows={2} />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Features v2</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <Input placeholder="Заголовок" value={newSolutionFeature.title} onChange={(e) => setNewSolutionFeature(prev => ({ ...prev, title: e.target.value }))} />
                          <Input placeholder="Группа" value={newSolutionFeature.group} onChange={(e) => setNewSolutionFeature(prev => ({ ...prev, group: e.target.value }))} />
                          <Textarea placeholder="Описание" value={newSolutionFeature.description} onChange={(e) => setNewSolutionFeature(prev => ({ ...prev, description: e.target.value }))} rows={2} className="col-span-2" />
                          <Input placeholder="Порядок" type="number" value={newSolutionFeature.order} onChange={(e) => setNewSolutionFeature(prev => ({ ...prev, order: e.target.value }))} />
                        </div>
                        <Button type="button" variant="outline" onClick={addSolutionFeature}>Добавить функцию</Button>
                        <div className="space-y-3">
                          {(productForm.features_v2 || []).map((feature, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Функция {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('features_v2', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Заголовок" value={feature.title || ''} onChange={(e) => updateArrayItem('features_v2', idx, { ...feature, title: e.target.value })} />
                              <Textarea placeholder="Описание" value={feature.description || ''} onChange={(e) => updateArrayItem('features_v2', idx, { ...feature, description: e.target.value })} rows={2} />
                              <div className="grid grid-cols-2 gap-3">
                                <Input placeholder="Группа" value={feature.group || ''} onChange={(e) => updateArrayItem('features_v2', idx, { ...feature, group: e.target.value })} />
                                <Input placeholder="Order" type="number" value={feature.order ?? idx + 1} onChange={(e) => updateArrayItem('features_v2', idx, { ...feature, order: Number(e.target.value || 0) })} />
                              </div>
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('features_v2', { title: '', description: '', group: 'core', order: (productForm.features_v2 || []).length + 1 })}>Добавить функцию</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Description blocks</Label>
                        <div className="space-y-3">
                          {(productForm.description_blocks || []).map((block, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Блок {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('description_blocks', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Заголовок" value={block.title || ''} onChange={(e) => updateArrayItem('description_blocks', idx, { ...block, title: e.target.value })} />
                              <Textarea placeholder="Текст" value={block.text || ''} onChange={(e) => updateArrayItem('description_blocks', idx, { ...block, text: e.target.value })} rows={3} />
                              <Input placeholder="Image URL" value={block.image_url || ''} onChange={(e) => updateArrayItem('description_blocks', idx, { ...block, image_url: e.target.value })} />
                              <Input placeholder="Video URL" value={block.video_url || ''} onChange={(e) => updateArrayItem('description_blocks', idx, { ...block, video_url: e.target.value })} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('description_blocks', { title: '', text: '', image_url: '', video_url: '', media_caption: '' })}>Добавить блок описания</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">How it works steps</Label>
                        <div className="space-y-3">
                          {(productForm.how_it_works_steps || []).map((step, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Шаг {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('how_it_works_steps', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Заголовок" value={step.title || ''} onChange={(e) => updateArrayItem('how_it_works_steps', idx, { ...step, title: e.target.value })} />
                              <Textarea placeholder="Описание" value={step.description || ''} onChange={(e) => updateArrayItem('how_it_works_steps', idx, { ...step, description: e.target.value })} rows={2} />
                              <Input placeholder="Иконка" value={step.icon || ''} onChange={(e) => updateArrayItem('how_it_works_steps', idx, { ...step, icon: e.target.value })} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('how_it_works_steps', { title: '', description: '', icon: '', order: (productForm.how_it_works_steps || []).length + 1 })}>Добавить шаг</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Integrations</Label>
                        <div className="space-y-3">
                          {(productForm.integrations || []).map((integration, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Интеграция {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('integrations', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Название" value={integration.name || ''} onChange={(e) => updateArrayItem('integrations', idx, { ...integration, name: e.target.value })} />
                              <Input placeholder="Logo URL" value={integration.logo_url || ''} onChange={(e) => updateArrayItem('integrations', idx, { ...integration, logo_url: e.target.value })} />
                              <Textarea placeholder="Описание" value={integration.description || ''} onChange={(e) => updateArrayItem('integrations', idx, { ...integration, description: e.target.value })} rows={2} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('integrations', { name: '', logo_url: '', description: '', order: (productForm.integrations || []).length + 1 })}>Добавить интеграцию</Button>
                        </div>
                      </div>
                    
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Documents</Label>
                        <div className="space-y-3">
                          {(productForm.documents || []).map((doc, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Документ {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('documents', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Название" value={doc.title || ''} onChange={(e) => updateArrayItem('documents', idx, { ...doc, title: e.target.value })} />
                              <Input placeholder="URL" value={doc.url || ''} onChange={(e) => updateArrayItem('documents', idx, { ...doc, url: e.target.value })} />
                              <Input placeholder="Type" value={doc.type || ''} onChange={(e) => updateArrayItem('documents', idx, { ...doc, type: e.target.value })} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('documents', { title: '', url: '', type: 'pdf' })}>Добавить документ</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Case studies</Label>
                        <div className="space-y-3">
                          {(productForm.case_studies || []).map((caseStudy, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Кейс {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('case_studies', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Название" value={caseStudy.title || ''} onChange={(e) => updateArrayItem('case_studies', idx, { ...caseStudy, title: e.target.value })} />
                              <Input placeholder="URL" value={caseStudy.url || ''} onChange={(e) => updateArrayItem('case_studies', idx, { ...caseStudy, url: e.target.value })} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('case_studies', { title: '', url: '' })}>Добавить кейс</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Benefits</Label>
                        <div className="space-y-3">
                          {(productForm.benefits || []).map((benefit, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Преимущество {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('benefits', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <Input placeholder="Заголовок" value={benefit.title || ''} onChange={(e) => updateArrayItem('benefits', idx, { ...benefit, title: e.target.value })} />
                                <Input placeholder="Иконка" value={benefit.icon || ''} onChange={(e) => updateArrayItem('benefits', idx, { ...benefit, icon: e.target.value })} />
                              </div>
                              <Textarea placeholder="Описание" value={benefit.description || ''} onChange={(e) => updateArrayItem('benefits', idx, { ...benefit, description: e.target.value })} rows={2} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('benefits', { title: '', description: '', icon: '', order: (productForm.benefits || []).length + 1 })}>Добавить преимущество</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">FAQ</Label>
                        <div className="space-y-3">
                          {(productForm.faq_items || []).map((faqItem, idx) => (
                            <div key={idx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Вопрос {idx + 1}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem('faq_items', idx)}><X className="h-4 w-4" /></Button>
                              </div>
                              <Input placeholder="Вопрос" value={faqItem.question || ''} onChange={(e) => updateArrayItem('faq_items', idx, { ...faqItem, question: e.target.value })} />
                              <Textarea placeholder="Ответ" value={faqItem.answer || ''} onChange={(e) => updateArrayItem('faq_items', idx, { ...faqItem, answer: e.target.value })} rows={2} />
                            </div>
                          ))}
                          <Button type="button" variant="outline" onClick={() => addArrayItem('faq_items', { question: '', answer: '', order: (productForm.faq_items || []).length + 1 })}>Добавить вопрос</Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Final CTA</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <Input placeholder="Badge text" value={productForm.final_cta?.badge_text || ''} onChange={(e) => setProductForm(prev => ({ ...prev, final_cta: { ...(prev.final_cta || {}), badge_text: e.target.value } }))} />
                          <Input placeholder="Title" value={productForm.final_cta?.title || ''} onChange={(e) => setProductForm(prev => ({ ...prev, final_cta: { ...(prev.final_cta || {}), title: e.target.value } }))} />
                          <Textarea placeholder="Text" value={productForm.final_cta?.text || ''} onChange={(e) => setProductForm(prev => ({ ...prev, final_cta: { ...(prev.final_cta || {}), text: e.target.value } }))} rows={2} className="col-span-2" />
                          <Input placeholder="Button label" value={productForm.final_cta?.button_label || ''} onChange={(e) => setProductForm(prev => ({ ...prev, final_cta: { ...(prev.final_cta || {}), button_label: e.target.value } }))} />
                          <Input placeholder="Secondary button label" value={productForm.final_cta?.secondary_button_label || ''} onChange={(e) => setProductForm(prev => ({ ...prev, final_cta: { ...(prev.final_cta || {}), secondary_button_label: e.target.value } }))} />
                          <Input placeholder="Price text" value={productForm.final_cta?.price_text || ''} onChange={(e) => setProductForm(prev => ({ ...prev, final_cta: { ...(prev.final_cta || {}), price_text: e.target.value } }))} />
                        </div>
                      </div>
                    </div>
                  )}
                  
{/* Legacy fields - only for old template */}
                {productForm.template_type === 'legacy' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Заголовок (Hero Title)</Label>
                        <Input
                          value={productForm.hero_title || ''}
                          onChange={(e) => setProductForm(prev => ({ ...prev, hero_title: e.target.value }))}
                          placeholder="Короткий заголовок решения"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Подсказка по цене</Label>
                        <Input
                          value={productForm.ai_pricing_hint || ''}
                          onChange={(e) => setProductForm(prev => ({ ...prev, ai_pricing_hint: e.target.value }))}
                          placeholder="от 50 000 ₽"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Описание (Hero Description)</Label>
                      <Textarea
                        value={productForm.hero_description || ''}
                        onChange={(e) => setProductForm(prev => ({ ...prev, hero_description: e.target.value }))}
                        placeholder="1-2 предложения о решении"
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>AI Summary (для LLM)</Label>
                      <Textarea
                        value={productForm.ai_summary || ''}
                        onChange={(e) => setProductForm(prev => ({ ...prev, ai_summary: e.target.value }))}
                        placeholder="Краткое резюме для ИИ (50-100 слов)"
                        rows={4}
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Сценарии использования (AI Use Cases)</Label>
                    <div className="space-y-2">
                      {productForm.ai_use_cases?.map((useCase, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={useCase}
                            onChange={(e) => {
                              const newUseCases = [...productForm.ai_use_cases];
                              newUseCases[idx] = e.target.value;
                              setProductForm(prev => ({ ...prev, ai_use_cases: newUseCases }));
                            }}
                            placeholder="Сценарий использования"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newUseCases = productForm.ai_use_cases.filter((_, i) => i !== idx);
                              setProductForm(prev => ({ ...prev, ai_use_cases: newUseCases }));
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setProductForm(prev => ({ 
                          ...prev, 
                          ai_use_cases: [...(prev.ai_use_cases || []), ''] 
                        }))}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Добавить сценарий
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Ключевые возможности (AI Features)</Label>
                    <div className="space-y-2">
                      {productForm.ai_features?.map((feature, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={feature}
                            onChange={(e) => {
                              const newFeatures = [...productForm.ai_features];
                              newFeatures[idx] = e.target.value;
                              setProductForm(prev => ({ ...prev, ai_features: newFeatures }));
                            }}
                            placeholder="Возможность"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newFeatures = productForm.ai_features.filter((_, i) => i !== idx);
                              setProductForm(prev => ({ ...prev, ai_features: newFeatures }));
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setProductForm(prev => ({ 
                          ...prev, 
                          ai_features: [...(prev.ai_features || []), ''] 
                        }))}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Добавить возможность
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Импортозамещение</Label>
                    <p className="text-xs text-muted-foreground">
                      Программы и продукты, которые замещает это решение
                    </p>
                    <div className="space-y-2">
                      {productForm.import_substitution?.map((program, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={program}
                            onChange={(e) => {
                              const next = [...productForm.import_substitution];
                              next[idx] = e.target.value;
                              setProductForm(prev => ({ ...prev, import_substitution: next }));
                            }}
                            placeholder="Например: Salesforce"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const next = productForm.import_substitution.filter((_, i) => i !== idx);
                              setProductForm(prev => ({ ...prev, import_substitution: next }));
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setProductForm(prev => ({
                          ...prev,
                          import_substitution: [...(prev.import_substitution || []), '']
                        }))}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Добавить программу
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>FAQ (Вопросы и ответы)</Label>
                    <div className="space-y-3">
                      {productForm.ai_faq?.map((faq, idx) => (
                        <div key={idx} className="p-3 bg-muted/50 rounded-lg space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Вопрос {idx + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                const newFaq = productForm.ai_faq.filter((_, i) => i !== idx);
                                setProductForm(prev => ({ ...prev, ai_faq: newFaq }));
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <Input
                            value={faq.q || ''}
                            onChange={(e) => {
                              const newFaq = [...productForm.ai_faq];
                              newFaq[idx] = { ...newFaq[idx], q: e.target.value };
                              setProductForm(prev => ({ ...prev, ai_faq: newFaq }));
                            }}
                            placeholder="Вопрос"
                          />
                          <Textarea
                            value={faq.a || ''}
                            onChange={(e) => {
                              const newFaq = [...productForm.ai_faq];
                              newFaq[idx] = { ...newFaq[idx], a: e.target.value };
                              setProductForm(prev => ({ ...prev, ai_faq: newFaq }));
                            }}
                            placeholder="Ответ"
                            rows={2}
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setProductForm(prev => ({ 
                          ...prev, 
                          ai_faq: [...(prev.ai_faq || []), { q: '', a: '' }] 
                        }))}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Добавить вопрос
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Ключевые слова (AI Keywords)</Label>
                    <Input
                      value={productForm.ai_keywords || ''}
                      onChange={(e) => setProductForm(prev => ({ ...prev, ai_keywords: e.target.value }))}
                      placeholder="AI, поиск, автоматизация, техподдержка"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Текст "Почему выбирают" (Benefits)</Label>
                    <Textarea
                      value={productForm.ai_benefits_text || ''}
                      onChange={(e) => setProductForm(prev => ({ ...prev, ai_benefits_text: e.target.value }))}
                      placeholder="Текст для секции 'Почему выбирают Антологию' (используется &lt;strong&gt; для выделения)"
                      rows={4}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Meta Title</Label>
                      <Input
                        value={productForm.meta_title || ''}
                        onChange={(e) => setProductForm(prev => ({ ...prev, meta_title: e.target.value }))}
                        placeholder="SEO заголовок"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Meta Description</Label>
                      <Input
                        value={productForm.meta_description || ''}
                        onChange={(e) => setProductForm(prev => ({ ...prev, meta_description: e.target.value }))}
                        placeholder="SEO описание"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                    <Label>Добавить доп. услугу</Label>
                    <div className="flex gap-2 items-start">
                      <Input
                        placeholder="Название"
                        value={newAdditionalService.name}
                        onChange={(e) => setNewAdditionalService(prev => ({ ...prev, name: e.target.value }))}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Описание"
                        value={newAdditionalService.description || ''}
                        onChange={(e) => setNewAdditionalService(prev => ({ ...prev, description: e.target.value }))}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Цена, ₽"
                        type="number"
                        value={newAdditionalService.price}
                        onChange={(e) => setNewAdditionalService(prev => ({ ...prev, price: e.target.value }))}
                        className="w-32"
                      />
                      <div className="flex items-center space-x-2 px-2">
                        <Checkbox
                          id="isMonthly"
                          checked={newAdditionalService.isMonthly || false}
                          onCheckedChange={(checked) => setNewAdditionalService(prev => ({ ...prev, isMonthly: Boolean(checked) }))}
                        />
                        <Label htmlFor="isMonthly" className="text-sm font-normal cursor-pointer whitespace-nowrap">
                          Ежемесячно
                        </Label>
                      </div>
                      <Button type="button" onClick={addAdditionalService}>Добавить</Button>
                    </div>
                    <div className="flex flex-col gap-2 mt-2">
                      {(productForm.calculator_config?.additionalServices || []).length > 0 ? (
                        (productForm.calculator_config.additionalServices || []).map((service, idx) => (
                          <div key={service.id || idx} className="flex flex-col gap-1 text-sm p-2 bg-muted/50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="flex-1 font-medium">{service.name || 'Без названия'}</span>
                              <Badge variant={service.isMonthly ? "default" : "outline"} className="text-[10px] px-1.5 py-0 h-4 leading-4">
                                {service.isMonthly ? "Ежемесячно" : "Разово"}
                              </Badge>
                              <span className="text-muted-foreground font-semibold">{Number(service.price || 0).toLocaleString('ru-RU')} ₽</span>
                              <Button variant="ghost" size="sm" onClick={() => removeAdditionalService(idx)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            {service.description && (
                              <span className="text-xs text-muted-foreground pl-1">{service.description}</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-muted-foreground p-2 bg-muted/30 rounded text-center">
                          Нет дополнительных услуг. Добавьте услуги выше.
                        </div>
                      )}
                    </div>
                  </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Иконка продукта</Label>
                    <div className="space-y-2">
                      <Select
                        value={isLucideIcon(productForm.icon) ? productForm.icon : ''}
                        onValueChange={(value) => setProductForm(prev => ({ ...prev, icon: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Выберите иконку или загрузите изображение" />
                        </SelectTrigger>
                        <SelectContent>
                          {LUCIDE_ICONS.map(icon => {
                            const IconComp = icon.Icon;
                            return (
                              <SelectItem key={icon.value} value={icon.value}>
                                <div className="flex items-center gap-2">
                                  <IconComp className="w-4 h-4" />
                                  <span>{icon.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <MediaManager
                        files={!isLucideIcon(productForm.icon) && productForm.icon ? [{ id: 'icon', url: productForm.icon, name: 'icon.png' }] : []}
                        onFilesChange={(files) => setProductForm(prev => ({ ...prev, icon: files.length > 0 ? files[0].url : '' }))}
                        maxFiles={1}
                        acceptedFileTypes={['image/*']}
                        showPreview={true}
                        onUpload={async (file) => {
                          const result = await mediaAPI.upload(file);
                          return result;
                        }}
                        uploading={uploadingIcon}
                        onUploadingChange={setUploadingIcon}
                      />
                    </div>
                  </div>
                <div className="space-y-2">
                  <Label>Цена</Label>
                  <Input
                    value={productForm.price || ''}
                    onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                  />
                </div>

                <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                  <Label className="text-base font-medium">Лицензии</Label>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <Input
                      placeholder="Название лицензии"
                      value={newLicense.name}
                      onChange={(e) => setNewLicense(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <Input
                      placeholder="Описание"
                      value={newLicense.description}
                      onChange={(e) => setNewLicense(prev => ({ ...prev, description: e.target.value }))}
                    />
                    <Input
                      placeholder="Доставка"
                      value={newLicense.delivery}
                      onChange={(e) => setNewLicense(prev => ({ ...prev, delivery: e.target.value }))}
                    />
                    <Input
                      placeholder="Период действия"
                      value={newLicense.minimumOrder}
                      onChange={(e) => setNewLicense(prev => ({ ...prev, minimumOrder: e.target.value }))}
                    />
                    <Input
                      placeholder="Стоимость, ₽"
                      type="number"
                      value={newLicense.price}
                      onChange={(e) => setNewLicense(prev => ({ ...prev, price: e.target.value }))}
                    />
                  </div>
                  <Button type="button" onClick={addLicense}>Добавить лицензию</Button>
                  <div className="flex flex-col gap-2 mt-2">
                    {(productForm.calculator_config?.licenses || []).length > 0 ? (
                      (productForm.calculator_config?.licenses || []).map((license, idx) => (
                        <div key={license.id || idx} className="flex flex-col gap-1 text-sm p-2 bg-muted/50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="flex-1 font-medium">{license.name || 'Без названия'}</span>
                            <span className="text-muted-foreground font-semibold">{license.price !== null && license.price !== undefined && license.price !== '' ? Number(license.price).toLocaleString('ru-RU') + ' ₽' : 'по запросу'}</span>
                            <Button variant="ghost" size="sm" onClick={() => removeLicense(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="text-xs text-muted-foreground flex flex-wrap gap-3 pl-1">
                            {license.description && <span>Описание: {license.description}</span>}
                            {license.delivery && <span>Доставка: {license.delivery}</span>}
                            {license.minimumOrder && <span>Период действия: {license.minimumOrder}</span>}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground p-2 bg-muted/30 rounded text-center">
                        Нет лицензий. Добавьте лицензии выше.
                      </div>
                    )}
                  </div>
                </div>
                  <div className="space-y-2">
                    <Label>Лендинг URL</Label>
                    <Input
                      value={productForm.landing_url || ''}
                      onChange={(e) => setProductForm(prev => ({ ...prev, landing_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Теги</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Добавить тег"
                    />
                    <Button type="button" onClick={addTag}>Добавить</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {productForm.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(index)} />
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Возможности</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      placeholder="Добавить возможность"
                    />
                    <Button type="button" onClick={addFeature}>Добавить</Button>
                  </div>
                  <div className="flex flex-col gap-2 mt-2">
                    {productForm.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="flex-1">{feature}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFeature(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                  <Label className="text-base font-medium">Решения (ситуации)</Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Выбрать из существующих</Label>
                      <Select
                        onValueChange={(value) => {
                          if (!productForm.situations.includes(value)) {
                            setProductForm(prev => ({
                              ...prev,
                              situations: [...prev.situations, value],
                            }));
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Выберите решение..." />
                        </SelectTrigger>
                        <SelectContent>
                          {situations
                            .filter(sit => !productForm.situations.includes(sit.id))
                            .map(sit => (
                              <SelectItem key={sit.id} value={sit.id}>{sit.name}</SelectItem>
                            ))}
                          {situations.filter(sit => !productForm.situations.includes(sit.id)).length === 0 && (
                            <div className="p-2 text-sm text-muted-foreground text-center">
                              Все решения добавлены
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Или создайте новое</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Название нового решения..."
                          value={newSituation}
                          onChange={(e) => setNewSituation(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSituation())}
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          onClick={addSituation} 
                          disabled={!newSituation.trim()}
                          className="whitespace-nowrap"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Создать
                        </Button>
                      </div>
                    </div>
                  </div>

                  {productForm.situations.length > 0 && (
                    <div className="pt-2 border-t border-border/50">
                      <Label className="text-sm text-muted-foreground mb-2 block">
                        Добавленные решения ({productForm.situations.length}):
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {productForm.situations.map((situationId) => {
                          const situation = situations.find(s => s.id === situationId);
                          return situation ? (
                            <Badge key={situationId} variant="default" className="flex items-center gap-2 py-1.5 px-3">
                              <span>{situation.name}</span>
                              <X
                                className="h-3 w-3 cursor-pointer hover:text-destructive"
                                onClick={() => setProductForm(prev => ({
                                  ...prev,
                                  situations: prev.situations.filter(id => id !== situationId),
                                }))}
                              />
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Блок "О вендоре" — логотип и HTML-описание задаются в разделе «Вендоры» */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-1">О вендоре</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Логотип и полное описание (HTML) задаются во вкладке «Вендоры».
                  </p>

                  <div className="space-y-4">
                    {/* Материалы о продукте */}
                    <div className="space-y-2">
                      <Label>Материалы о продукте</Label>
                      <MediaManager
                        files={productForm.calculator_config?.vendor_info?.materials || []}
                        onFilesChange={(files) => setProductForm(prev => ({
                          ...prev,
                          calculator_config: {
                            ...prev.calculator_config,
                            vendor_info: {
                              ...(prev.calculator_config?.vendor_info || {}),
                              materials: files,
                            },
                          },
                        }))}
                        maxFiles={10}
                        acceptedFileTypes={['image/*', '.pdf', '.xlsx', '.xls', '.doc', '.docx']}
                        showPreview={true}
                        onUpload={async (file) => {
                          // Без onUpload MediaManager хранит `URL.createObjectURL(file)` (blob:...),
                          // который у публичных посетителей невалиден. Поэтому загружаем файл на сервер
                          // и возвращаем стабильный `/images/materials/...` URL.
                          const productId = editingProduct?.id;
                          if (!productId) {
                            throw new Error('Сначала сохраните продукт, чтобы загрузить материалы.');
                          }

                          const uploaded = await adminAPI.uploadProductFile(productId, file);
                          return {
                            id: uploaded.id,
                            name: uploaded.file_name || file.name,
                            type: uploaded.file_type || file.type,
                            size: uploaded.file_size || file.size,
                            url: productFilePublicUrl(uploaded.file_path),
                          };
                        }}
                      />
                    </div>

                    {/* Условия поставки */}
                    <div className="space-y-2">
                      <Label>Условия поставки</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Условие (например: Срок поставки)"
                          value={newVendorDeliveryTerm.condition}
                          onChange={(e) => setNewVendorDeliveryTerm(prev => ({ ...prev, condition: e.target.value }))}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Значение (например: 1-3 рабочих дня)"
                          value={newVendorDeliveryTerm.value}
                          onChange={(e) => setNewVendorDeliveryTerm(prev => ({ ...prev, value: e.target.value }))}
                          className="flex-1"
                        />
                        <Button type="button" onClick={addVendorDeliveryTerm}>Добавить</Button>
                      </div>
                      <div className="flex flex-col gap-2 mt-2">
                        {Array.isArray(productForm.calculator_config?.vendor_info?.deliveryTerms) && productForm.calculator_config.vendor_info.deliveryTerms.length > 0 ? (
                          Array.isArray(productForm.calculator_config?.vendor_info?.deliveryTerms) && productForm.calculator_config.vendor_info.deliveryTerms.map((term, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded">
                              <span className="font-medium w-40">{term.condition}:</span>
                              <span className="flex-1 text-muted-foreground">{term.value}</span>
                              <Button variant="ghost" size="sm" onClick={() => removeVendorDeliveryTerm(idx)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-muted-foreground p-2 bg-muted/30 rounded text-center">
                            Нет условий поставки. Добавьте условия выше.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                    disabled={loading}
                  >
                    {editingProduct ? 'Сохранить' : 'Создать'}
                  </Button>
                  {editingProduct && (
                    <Button variant="outline" onClick={resetProductForm}>
                      Отмена
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Список продуктов</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Название</TableHead>
                      <TableHead>Категория</TableHead>
                      <TableHead>Цена</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>
                          {categories.find(c => c.id === product.category)?.name || product.category}
                        </TableCell>
                        <TableCell>{product.price}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Copy product
                                const copyData = {
                                  ...products.find(p => p.id === product.id),
                                  id: `${product.id}-copy-${Date.now()}`,
                                  name: `${product.name} (копия)`,
                                  sku: '',
                                };
                                setSelectedProductIndex(copyData.id);
                                setProductForm(prev => ({
                                  ...prev,
                                  ...copyData,
                                }));
                              }}
                              title="Копировать"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProduct(product)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => loadProductFiles(product.id)}
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Файлы продукта: {product.name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div className="space-y-2">
                                    <Label>Загрузить файл</Label>
                                    <Input
                                      type="file"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          handleFileUpload(product.id, file);
                                          e.target.value = ''; // Сброс input
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Файлы продукта</Label>
                                    <div className="space-y-2">
                                      {(productFiles[product.id] || []).length === 0 ? (
                                        <p className="text-sm text-gray-500">Нет загруженных файлов</p>
                                      ) : (
                                        (productFiles[product.id] || []).map((file) => (
                                          <div key={file.id} className="flex items-center justify-between p-2 border rounded">
                                            <div className="flex items-center gap-2">
                                              <File className="h-4 w-4" />
                                              <a
                                                href={productFilePublicUrl(file.file_path)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                              >
                                                {file.file_name}
                                              </a>
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleDeleteFile(product.id, file.id)}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vendors" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle className="text-base">Синхронизация из каталога</CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const report = await adminVendorsAPI.syncFromProducts();
                      const vendorsData = await adminVendorsAPI.getVendors();
                      setVendors(vendorsData || []);
                      toast({
                        title: 'Вендоры синхронизированы',
                        description: `Создано: ${report.vendors_created_count ?? 0}, нормализовано в продуктах: ${report.products_vendor_normalized ?? 0}`,
                      });
                    } catch (err) {
                      toast({
                        title: 'Ошибка синхронизации',
                        description: err.message,
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Подтянуть из продуктов
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Создаёт записи вендоров по полю vendor в продуктах и объединяет дубли (регистр и первое слово, например «just ai» и «JUST AI»).
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{editingVendor ? 'Редактировать вендора' : 'Создать вендора'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Лаборатория Касперского"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slug (URL)</Label>
                    <Input
                      value={vendorForm.slug}
                      onChange={(e) => setVendorForm(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="kaspersky"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Логотип</Label>
                  <MediaManager
                    files={vendorForm.logo ? [{ id: 'logo', url: vendorForm.logo, name: 'logo.png' }] : []}
                    onFilesChange={(files) => setVendorForm(prev => ({ ...prev, logo: files.length > 0 ? files[0].url : '' }))}
                    maxFiles={1}
                    acceptedFileTypes={['image/*']}
                    showPreview={true}
                    onUpload={async (file) => {
                      const result = await mediaAPI.upload(file);
                      return result;
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Сертификат</Label>
                  <p className="text-xs text-muted-foreground">Изображение (PNG, JPG, WebP) или PDF</p>
                  <MediaManager
                    files={vendorForm.certificate ? [{
                      id: 'certificate',
                      url: vendorForm.certificate,
                      name: vendorForm.certificate.split('/').pop() || 'certificate',
                    }] : []}
                    onFilesChange={(files) => setVendorForm(prev => ({ ...prev, certificate: files.length > 0 ? files[0].url : '' }))}
                    maxFiles={1}
                    maxFileSize={15728640}
                    acceptedFileTypes={['image/*', '.pdf', 'application/pdf']}
                    showPreview={true}
                    onUpload={async (file) => {
                      const result = await mediaAPI.upload(file);
                      return result;
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Краткое описание</Label>
                  <Input
                    value={vendorForm.description}
                    onChange={(e) => setVendorForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Антивирусная защита для бизнеса"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Полное описание из решения</Label>
                  <RichTextEditor
                    value={vendorForm.full_description || ''}
                    onChange={(value) => setVendorForm(prev => ({ ...prev, full_description: value }))}
                    placeholder="Описание для блока «О вендоре» на странице решения..."
                    onUploadImage={async (file) => {
                      try {
                        const result = await mediaAPI.upload(file);
                        return result;
                      } catch (error) {
                        console.error('Upload error:', error);
                        throw error;
                      }
                    }}
                  />
                </div>
                <AdminSeoFields
                  form={vendorForm}
                  setForm={setVendorForm}
                  idPrefix="vendor"
                  onFillTemplate={() => {
                    const preview = buildVendorSeo(
                      {
                        name: vendorForm.name,
                        slug: vendorForm.slug,
                        description: vendorForm.description,
                        full_description: vendorForm.full_description,
                        seo_primary_query: vendorForm.seo_primary_query,
                      },
                      [],
                    );
                    setVendorForm((prev) => ({
                      ...prev,
                      meta_title: preview.title,
                      meta_description: preview.description,
                      meta_keywords: preview.keywords,
                    }));
                    toast({
                      title: 'SEO по шаблону',
                      description: 'Поля meta заполнены. Проверьте якоря перед сохранением.',
                    });
                  }}
                />
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="vendorIsActive"
                      checked={vendorForm.is_active}
                      onCheckedChange={(checked) => setVendorForm(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label htmlFor="vendorIsActive">Активен</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="vendorIsTop"
                      checked={vendorForm.is_top || false}
                      onCheckedChange={(checked) => setVendorForm(prev => ({ ...prev, is_top: checked }))}
                    />
                    <Label htmlFor="vendorIsTop">ТОП</Label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        if (editingVendor) {
                          await adminVendorsAPI.update(editingVendor.id, {
                            ...vendorForm,
                            name: (vendorForm.name || '').trim(),
                            slug: (vendorForm.slug || '').trim(),
                            description: (vendorForm.description || '').trim(),
                            full_description: vendorForm.full_description || '',
                            logo: vendorForm.logo || null,
                            certificate: vendorForm.certificate || null,
                            is_active: !!vendorForm.is_active,
                            is_top: !!vendorForm.is_top,
                            meta_title: vendorForm.meta_title?.trim() || null,
                            meta_description: vendorForm.meta_description?.trim() || null,
                            meta_keywords: vendorForm.meta_keywords?.trim() || null,
                            seo_primary_query: vendorForm.seo_primary_query?.trim() || null,
                          });
                          toast({ title: 'Вендор обновлён' });
                        } else {
                          await adminVendorsAPI.create(vendorForm);
                          toast({ title: 'Вендор создан' });
                        }
                        setVendorForm({
                          name: '',
                          slug: '',
                          logo: '',
                          certificate: '',
                          description: '',
                          full_description: '',
                          is_active: true,
                          is_top: false,
                        });
                        setEditingVendor(null);
                        const vendorsData = await adminVendorsAPI.getVendors();
                        setVendors(vendorsData || []);
                      } catch (err) {
                        toast({ title: 'Ошибка', description: err.message, variant: 'destructive' });
                      }
                    }}
                  >
                    {editingVendor ? 'Обновить' : 'Создать'}
                  </Button>
                  {editingVendor && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingVendor(null);
                        setVendorForm({
                          name: '',
                          slug: '',
                          logo: '',
                          certificate: '',
                          description: '',
                          full_description: '',
                          is_active: true,
                          is_top: false,
                        });
                      }}
                    >
                      Отмена
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Список вендоров</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Лого</TableHead>
                      <TableHead>Активен</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendors.map((vendor) => (
                      <TableRow key={vendor.id}>
                        <TableCell>{vendor.id}</TableCell>
                        <TableCell>{vendor.name}</TableCell>
                        <TableCell>{vendor.slug}</TableCell>
                        <TableCell>
                          {vendor.logo && (
                            <img src={vendor.logo} alt="" className="h-8 w-8 object-contain" />
                          )}
                        </TableCell>
                        <TableCell>{vendor.is_active ? 'Да' : 'Нет'}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingVendor(vendor);
                                setVendorForm({
                                  name: vendor.name,
                                  slug: vendor.slug,
                                  logo: vendor.logo || '',
                                  certificate: vendor.certificate || '',
                                  description: vendor.description || '',
                                  full_description: vendor.full_description || '',
                                  is_active: vendor.is_active === true || vendor.is_active === 'true',
                                  is_top: vendor.is_top === true || vendor.is_top === 'true',
                                  meta_title: vendor.meta_title || '',
                                  meta_description: vendor.meta_description || '',
                                  meta_keywords: vendor.meta_keywords || '',
                                  seo_primary_query: vendor.seo_primary_query || '',
                                });
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm('Удалить вендора?')) {
                                  try {
                                    await adminVendorsAPI.delete(vendor.id);
                                    const vendorsData = await adminVendorsAPI.getVendors();
                                    setVendors(vendorsData || []);
                                    toast({ title: 'Вендор удалён' });
                                  } catch (err) {
                                    toast({ title: 'Ошибка', description: err.message, variant: 'destructive' });
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="news" className="space-y-4">
            <AdminNewsTab />
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4">
            <AdminSubscriptionsTab />
          </TabsContent>

          <TabsContent value="landings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{editingLanding ? 'Редактировать лендинг' : 'Создать лендинг'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Slug (URL)</Label>
                    <Input
                      value={landingForm.slug}
                      onChange={(e) => setLandingForm(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="crm-deals"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={landingForm.title}
                      onChange={(e) => setLandingForm(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Hero заголовок</Label>
                  <Input
                    value={landingForm.hero_title}
                    onChange={(e) => setLandingForm(prev => ({ ...prev, hero_title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hero описание</Label>
                  <Textarea
                    value={landingForm.hero_description}
                    onChange={(e) => setLandingForm(prev => ({ ...prev, hero_description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ситуации (фильтр)</Label>
                  <Select
                    onValueChange={(value) => {
                      if (!landingForm.situations.includes(value)) {
                        setLandingForm(prev => ({ ...prev, situations: [...prev.situations, value] }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите ситуацию" />
                    </SelectTrigger>
                    <SelectContent>
                      {landingSituations
                        .filter(sit => !landingForm.situations.includes(sit.id))
                        .map(sit => (
                          <SelectItem key={sit.id} value={sit.id}>{sit.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {landingForm.situations.map((situationId) => {
                      const situation = landingSituations.find(s => s.id === situationId);
                      return situation ? (
                        <Badge key={situationId} variant="secondary" className="flex items-center gap-1">
                          {situation.name}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => setLandingForm(prev => ({
                              ...prev,
                              situations: prev.situations.filter(id => id !== situationId),
                            }))}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Meta описание</Label>
                  <Textarea
                    value={landingForm.meta_description}
                    onChange={(e) => setLandingForm(prev => ({ ...prev, meta_description: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={landingForm.is_active}
                    onChange={(e) => setLandingForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label>Активен</Label>
                </div>
                <div className="space-y-2">
                  <Label>Теги</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newLandingTag}
                      onChange={(e) => setNewLandingTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLandingTag())}
                      placeholder="Добавить тег"
                    />
                    <Button type="button" onClick={addLandingTag}>Добавить</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {landingForm.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeLandingTag(index)} />
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingLanding ? handleUpdateLanding : handleCreateLanding}
                    disabled={loading}
                  >
                    {editingLanding ? 'Сохранить' : 'Создать'}
                  </Button>
                  {editingLanding && (
                    <Button variant="outline" onClick={resetLandingForm}>
                      Отмена
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Список лендингов</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slug</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {landings.map((landing) => (
                      <TableRow key={landing.id}>
                        <TableCell className="font-mono text-sm">{landing.slug}</TableCell>
                        <TableCell>{landing.title}</TableCell>
                        <TableCell>
                          <Badge variant={landing.is_active ? "default" : "secondary"}>
                            {landing.is_active ? 'Активен' : 'Неактивен'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditLanding(landing)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Редактировать
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`/landing/${landing.slug}`, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Открыть
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteLanding(landing.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {editingLanding && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Сервисы</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="ID сервиса"
                        value={newService.service_id}
                        onChange={(e) => setNewService(prev => ({ ...prev, service_id: e.target.value }))}
                      />
                      <Input
                        placeholder="Название"
                        value={newService.name}
                        onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <Textarea
                      placeholder="Описание"
                      value={newService.description}
                      onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                    />
                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        placeholder="Иконка"
                        value={newService.icon}
                        onChange={(e) => setNewService(prev => ({ ...prev, icon: e.target.value }))}
                      />
                      <Input
                        placeholder="Цена"
                        type="number"
                        value={newService.price}
                        onChange={(e) => setNewService(prev => ({ ...prev, price: e.target.value }))}
                      />
                      <Select
                        value={newService.price_unit}
                        onValueChange={(value) => setNewService(prev => ({ ...prev, price_unit: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="/мес">/мес</SelectItem>
                          <SelectItem value=" единоразово"> единоразово</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={addService}>Добавить сервис</Button>
                    <div className="space-y-2">
                      {landingForm.services.map((service, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-semibold">{service.name}</div>
                            <div className="text-sm text-gray-500">{service.description}</div>
                            <div className="text-sm">{service.price} {service.price_unit}</div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeService(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Преимущества</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Input
                      placeholder="Заголовок"
                      value={newBenefit.title}
                      onChange={(e) => setNewBenefit(prev => ({ ...prev, title: e.target.value }))}
                    />
                    <Textarea
                      placeholder="Описание"
                      value={newBenefit.description}
                      onChange={(e) => setNewBenefit(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                    />
                    <Input
                      placeholder="Иконка"
                      value={newBenefit.icon}
                      onChange={(e) => setNewBenefit(prev => ({ ...prev, icon: e.target.value }))}
                    />
                    <Button onClick={addBenefit}>Добавить преимущество</Button>
                    <div className="space-y-2">
                      {landingForm.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-semibold">{benefit.title}</div>
                            <div className="text-sm text-gray-500">{benefit.description}</div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeBenefit(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Материалы</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Input
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleLandingFileUpload(editingLanding.id, file);
                            }
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        {landingFiles[editingLanding.id]?.map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-2 border rounded">
                            <div className="flex items-center gap-2">
                              <File className="h-4 w-4" />
                              <span>{file.file_name}</span>
                            </div>
                            <div className="flex gap-2">
                              <a
                                href={`${API_BASE_URL}/uploads/${file.file_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button variant="outline" size="sm">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </a>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteLandingFile(editingLanding.id, file.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="homeSettings" className="space-y-4">

            <Card>
              <CardHeader>
                <CardTitle>Категории каталога</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Категории используются в каталоге и при назначении решения. ID задаётся латиницей (например, <code className="text-xs">data_security</code>).
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID</Label>
                    <Input
                      value={categoryForm.id}
                      onChange={(e) => setCategoryForm((prev) => ({ ...prev, id: e.target.value }))}
                      placeholder="data_security"
                      disabled={!!editingCategory}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setCategoryForm((prev) => ({
                          ...prev,
                          name,
                          id: editingCategory ? prev.id : (prev.id || slugifyCategoryId(name)),
                        }));
                      }}
                      placeholder="Защита данных"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Картинка категории</Label>
                    <MediaManager
                      files={categoryForm.icon ? [{ id: 'category-icon', url: categoryForm.icon, name: 'category-icon' }] : []}
                      onFilesChange={(files) =>
                        setCategoryForm((prev) => ({
                          ...prev,
                          icon: files.length > 0 ? files[0].url : '',
                        }))
                      }
                      maxFiles={1}
                      acceptedFileTypes={['image/*']}
                      showPreview={true}
                      onUpload={async (file) => {
                        const result = await mediaAPI.upload(file);
                        return result;
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Описание</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) =>
                        setCategoryForm((prev) => ({ ...prev, description: e.target.value }))
                      }
                      placeholder="Краткое описание категории"
                      rows={4}
                    />
                  </div>
                  <AdminSeoFields form={categoryForm} setForm={setCategoryForm} idPrefix="category" />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                    disabled={loading}
                  >
                    {editingCategory ? 'Сохранить' : 'Создать'}
                  </Button>
                  {editingCategory && (
                    <Button variant="outline" onClick={resetCategoryForm}>
                      Отмена
                    </Button>
                  )}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead>Картинка</TableHead>
                      <TableHead>Описание</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filterCategories.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-xs">{item.id}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          {item.icon ? (
                            <img src={item.icon} alt={item.name} className="h-8 w-8 rounded object-cover border" />
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {item.description || <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditCategory(item)}
                              disabled={item.id === 'all'}
                            >
                              <Edit className="h-4 w-4 mr-1" /> Редактировать
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteCategory(item.id)}
                              disabled={item.id === 'all'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Разделы каталога</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Раздел привязан к категории и формирует URL вида <code className="text-xs">/catalog/категория/slug</code>.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Категория</Label>
                    <Select
                      value={sectionForm.category_id || ''}
                      onValueChange={(value) =>
                        setSectionForm((prev) => ({ ...prev, category_id: value }))
                      }
                      disabled={!!editingSection}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите категорию" />
                      </SelectTrigger>
                      <SelectContent>
                        {filterCategories.filter((c) => c.id !== 'all').map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Slug</Label>
                    <Input
                      value={sectionForm.slug}
                      onChange={(e) => setSectionForm((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="data-protection"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={sectionForm.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setSectionForm((prev) => ({
                          ...prev,
                          name,
                          slug: prev.slug || slugifySectionSlug(name),
                        }));
                      }}
                      placeholder="Защита данных"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Картинка раздела</Label>
                    <MediaManager
                      files={sectionForm.icon ? [{ id: 'section-icon', url: sectionForm.icon, name: 'section-icon' }] : []}
                      onFilesChange={(files) =>
                        setSectionForm((prev) => ({
                          ...prev,
                          icon: files.length > 0 ? files[0].url : '',
                        }))
                      }
                      maxFiles={1}
                      acceptedFileTypes={['image/*']}
                      showPreview={true}
                      onUpload={async (file) => {
                        const result = await mediaAPI.upload(file);
                        return result;
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Описание</Label>
                    <RichTextEditor
                      value={sectionForm.description}
                      onChange={(value) =>
                        setSectionForm((prev) => ({ ...prev, description: value }))
                      }
                      placeholder="Описание раздела"
                    />
                  </div>
                  <AdminSeoFields form={sectionForm} setForm={setSectionForm} idPrefix="section" />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingSection ? handleUpdateSection : handleCreateSection}
                    disabled={loading}
                  >
                    {editingSection ? 'Сохранить' : 'Создать'}
                  </Button>
                  {editingSection && (
                    <Button variant="outline" onClick={resetSectionForm}>
                      Отмена
                    </Button>
                  )}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Категория</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead>Картинка</TableHead>
                      <TableHead>Описание</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminSectionsList.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>{filterCategories.find((c) => c.id === s.category_id)?.name || s.category_id}</TableCell>
                        <TableCell className="font-mono text-xs">{s.slug}</TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>
                          {s.icon ? (
                            <img src={s.icon} alt={s.name} className="h-8 w-8 rounded object-cover border" />
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {s.description || <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditSection(s)}>
                              <Edit className="h-4 w-4 mr-1" /> Редактировать
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteSection(s.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Фильтр по типу решения (ситуации)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID</Label>
                    <Input
                      value={situationForm.id}
                      onChange={(e) => setSituationForm(prev => ({ ...prev, id: e.target.value }))}
                      placeholder="crm-deals"
                      disabled={!!editingSituation}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={situationForm.name}
                      onChange={(e) => setSituationForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Иконка (lucide)</Label>
                    <Input
                      value={situationForm.icon}
                      onChange={(e) => setSituationForm(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="TrendingUp"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Описание</Label>
                    <Input
                      value={situationForm.description}
                      onChange={(e) => setSituationForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Краткое описание"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingSituation ? handleUpdateSituation : handleCreateSituation}
                    disabled={loading}
                  >
                    {editingSituation ? 'Сохранить' : 'Создать'}
                  </Button>
                  {editingSituation && (
                    <Button variant="outline" onClick={resetSituationForm}>
                      Отмена
                    </Button>
                  )}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead>Иконка</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filterSituations.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-xs">{item.id}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="text-xs">{item.icon}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditSituation(item)}>
                              <Edit className="h-4 w-4 mr-1" /> Редактировать
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteSituation(item.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Заказы</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Номер заказа</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Организация</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Дата</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow 
                        key={order.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/utech/orders/${order.id}`)}
                      >
                        <TableCell 
                          className="font-medium text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2">
                            {order.order_number}
                            <ExternalLink className="h-3 w-3" />
                          </div>
                        </TableCell>
                        <TableCell>{order.email}</TableCell>
                        <TableCell>{order.organization_name}</TableCell>
                        <TableCell>
                          <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                            {getStatusLabel(order.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={order.status}
                            onValueChange={(newStatus) => {
                              adminAPI.updateOrderStatus(order.id, newStatus).then(() => {
                                loadData();
                              });
                            }}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Выберите статус" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">В ожидании</SelectItem>
                              <SelectItem value="processing">В обработке</SelectItem>
                              <SelectItem value="completed">Завершен</SelectItem>
                              <SelectItem value="cancelled">Отменен</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cart" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Заказы в корзине пользователей</CardTitle>
                <CardDescription>
                  Список товаров, которые пользователи добавили в свои корзины, но еще не оформили
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <p>Загрузка данных...</p>
                  </div>
                ) : !cartOrders || cartOrders.length === 0 ? (
                  <Alert>
                    <AlertDescription>
                      В данный момент нет товаров в корзинах пользователей
                      {process.env.NODE_ENV === 'development' && (
                        <div className="mt-2 text-xs text-gray-500">
                          Debug: cartOrders = {JSON.stringify(cartOrders)}
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Пользователь / Организация</TableHead>
                          <TableHead>Продукт</TableHead>
                          <TableHead>Данные калькулятора</TableHead>
                          <TableHead>Контакты</TableHead>
                          <TableHead>Дата добавления</TableHead>
                          <TableHead>Действия</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cartOrders && cartOrders.length > 0 ? (
                          cartOrders.map((item) => (
                            <TableRow key={item.id || `cart-${item.user_id}-${item.product_id}`}>
                              <TableCell className="font-medium">
                                <div className="space-y-1">
                                  <div className="font-semibold">{item.user_email || 'Неизвестный пользователь'}</div>
                                  {item.organization_name && (
                                    <div className="text-sm text-gray-600">{item.organization_name}</div>
                                  )}
                                  {item.user_id && (
                                    <div className="text-xs text-gray-400">ID: {item.user_id.substring(0, 8)}...</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">{item.product_name || 'Неизвестный продукт'}</div>
                                  {item.product_id && (
                                    <div className="text-xs text-gray-500">ID: {item.product_id}</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {item.calculator_data && typeof item.calculator_data === 'object' ? (
                                  <div className="space-y-1 text-sm">
                                    {item.calculator_data.monthlyPrice !== undefined && item.calculator_data.monthlyPrice !== null && (
                                      <div>Ежемесячно: {item.calculator_data.monthlyPrice} ₽</div>
                                    )}
                                    {item.calculator_data.oneTimePrice !== undefined && item.calculator_data.oneTimePrice !== null && (
                                      <div>Единоразово: {item.calculator_data.oneTimePrice} ₽</div>
                                    )}
                                    {item.calculator_data.quantity !== undefined && item.calculator_data.quantity !== null && (
                                      <div>Количество: {item.calculator_data.quantity}</div>
                                    )}
                                    {(!item.calculator_data.monthlyPrice && 
                                      !item.calculator_data.oneTimePrice && 
                                      !item.calculator_data.quantity) && (
                                      <span className="text-gray-400 text-xs">Данные калькулятора отсутствуют</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-sm">Нет данных</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1 text-sm">
                                  {item.contact_phone && (
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium">Тел:</span>
                                      <span>{item.contact_phone}</span>
                                    </div>
                                  )}
                                  {item.company_email && item.company_email !== item.user_email && (
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium">Email:</span>
                                      <span className="text-xs break-all">{item.company_email}</span>
                                    </div>
                                  )}
                                  {item.user_email && !item.company_email && (
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium">Email:</span>
                                      <span className="text-xs break-all">{item.user_email}</span>
                                    </div>
                                  )}
                                  {!item.contact_phone && !item.company_email && !item.user_email && (
                                    <span className="text-gray-400 text-xs">Контакты не указаны</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {item.created_at ? (
                                  (() => {
                                    try {
                                      const date = new Date(item.created_at);
                                      if (!isNaN(date.getTime())) {
                                        return (
                                          <div>
                                            <div>{date.toLocaleDateString('ru-RU')}</div>
                                            <div className="text-xs text-gray-500">
                                              {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                          </div>
                                        );
                                      }
                                    } catch (e) {
                                      console.error('Error parsing date:', e);
                                    }
                                    return <span className="text-gray-400 text-sm">{item.created_at}</span>;
                                  })()
                                ) : (
                                  <span className="text-gray-400 text-sm">Не указано</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    toast({
                                      title: "Информация о товаре в корзине",
                                      description: (
                                        <div className="mt-2 space-y-2 text-sm">
                                          <div><strong>Пользователь:</strong> {item.user_email || 'Не указан'}</div>
                                        {item.organization_name && (
                                          <div><strong>Организация:</strong> {item.organization_name}</div>
                                        )}
                                          {item.contact_phone && (
                                            <div><strong>Телефон:</strong> {item.contact_phone}</div>
                                          )}
                                          {item.company_email && (
                                            <div><strong>Email компании:</strong> {item.company_email}</div>
                                          )}
                                          <div><strong>Продукт:</strong> {item.product_name || 'Не указан'}</div>
                                          {item.calculator_data && typeof item.calculator_data === 'object' && (
                                            <div className="mt-2">
                                              <strong>Данные калькулятора:</strong>
                                              <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
                                                {JSON.stringify(item.calculator_data, null, 2)}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  Детали
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                              Нет данных для отображения
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {!loading && cartOrders && cartOrders.length > 0 && (
                  <div className="mt-4 text-sm text-gray-500">
                    Всего товаров в корзинах: {cartOrders.length}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="purchases" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Покупки</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Номер заказа</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Продукт</TableHead>
                      <TableHead>Цена</TableHead>
                      <TableHead>Дата покупки</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchases.map((purchase) => (
                      <TableRow 
                        key={purchase.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          if (purchase.order_number) {
                            navigate(`/utech/orders/by-number/${purchase.order_number}`);
                          }
                        }}
                      >
                        <TableCell 
                          className="font-medium text-blue-600 hover:text-blue-800"
                        >
                          <div className="flex items-center gap-2">
                            {purchase.order_number}
                            {purchase.order_number && <ExternalLink className="h-3 w-3" />}
                          </div>
                        </TableCell>
                        <TableCell>{purchase.user_email}</TableCell>
                        <TableCell>{purchase.product_name}</TableCell>
                        <TableCell>
                          {purchase.monthly_price && `Ежемесячно: ${purchase.monthly_price} руб.`}
                          {purchase.one_time_price && ` Единоразово: ${purchase.one_time_price} руб.`}
                        </TableCell>
                        <TableCell>{new Date(purchase.purchase_date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanelPage;
